/**
 * Browser half of passkey sign-in (#387).
 *
 * Kept out of the views because both of them need it — LoginView to assert, the
 * account page to register — and because the base64url ⇄ ArrayBuffer conversion
 * is the part that is easy to get subtly wrong and impossible to see wrong.
 *
 * `navigator.credentials` speaks ArrayBuffers; the server speaks base64url
 * (what `@simplewebauthn/server` produces and expects). The whole file is that
 * translation plus the two ceremonies.
 */

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

function b64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The shape `@simplewebauthn/server` verifies. */
export interface AssertionPayload {
  id: string;
  rawId: string;
  type: string;
  clientExtensionResults: Record<string, unknown>;
  response: Record<string, string | string[] | undefined>;
}

function serializeCredential(credential: PublicKeyCredential): AssertionPayload {
  const response = credential.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const payload: AssertionPayload = {
    id: credential.id,
    rawId: bufferToB64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
    response: {
      clientDataJSON: bufferToB64url(response.clientDataJSON),
    },
  };
  if (response.attestationObject) {
    payload.response.attestationObject = bufferToB64url(response.attestationObject);
    // `getTransports` is how the server learns to give the browser a better
    // prompt next time. Not every browser has it, and its absence is not an
    // error — the passkey works either way.
    if (typeof response.getTransports === 'function') {
      payload.response.transports = response.getTransports();
    }
  }
  if (response.authenticatorData) payload.response.authenticatorData = bufferToB64url(response.authenticatorData);
  if (response.signature) payload.response.signature = bufferToB64url(response.signature);
  if (response.userHandle) payload.response.userHandle = bufferToB64url(response.userHandle);
  return payload;
}

// A browser runs ONE WebAuthn request at a time, per PAGE — not per view. A
// ceremony still pending when the next one starts makes the next one fail at
// once ("A request is already pending"), and in a single-page app the pending
// one can belong to a view that is long gone: the login page's autofill ceremony
// outliving the login page is exactly how "Add a passkey" on the account page
// came to do nothing. So the slot is owned HERE, where both ceremonies start:
// claiming it calls off whoever held it, whichever view that was.
let activeCeremony: AbortController | null = null;

function claimCeremony(external?: AbortSignal): AbortController {
  activeCeremony?.abort();
  const controller = new AbortController();
  activeCeremony = controller;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller;
}

function releaseCeremony(controller: AbortController) {
  if (activeCeremony === controller) activeCeremony = null;
}

/** Registration ceremony: turn the server's options into a new credential. */
export async function createPasskey(options: any): Promise<AssertionPayload> {
  const ceremony = claimCeremony();
  try {
    return await runCreate(options, ceremony.signal);
  } finally {
    releaseCeremony(ceremony);
  }
}

async function runCreate(options: any, signal: AbortSignal): Promise<AssertionPayload> {
  const credential = (await navigator.credentials.create({
    signal,
    publicKey: {
      ...options,
      challenge: b64urlToBuffer(options.challenge),
      user: { ...options.user, id: b64urlToBuffer(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
        ...c,
        id: b64urlToBuffer(c.id),
      })),
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('passkey registration was cancelled');
  return serializeCredential(credential);
}

/**
 * Authentication ceremony.
 *
 * `mediation: 'conditional'` is the autofill variant: the browser offers the
 * passkey inside the e-mail field rather than behind a button, and — critically —
 * it must NOT be treated as a failure when the person types a password instead.
 * The caller aborts it; that abort is expected, not an error.
 */
export async function getPasskeyAssertion(
  options: any,
  opts: { conditional?: boolean; signal?: AbortSignal } = {},
): Promise<AssertionPayload> {
  const ceremony = claimCeremony(opts.signal);
  try {
    return await runGet(options, { conditional: opts.conditional, signal: ceremony.signal });
  } finally {
    releaseCeremony(ceremony);
  }
}

async function runGet(
  options: any,
  opts: { conditional?: boolean; signal: AbortSignal },
): Promise<AssertionPayload> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: b64urlToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((c: any) => ({
        ...c,
        id: b64urlToBuffer(c.id),
      })),
    },
    ...(opts.conditional ? { mediation: 'conditional' as CredentialMediationRequirement } : {}),
    signal: opts.signal,
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('passkey sign-in was cancelled');
  return serializeCredential(credential);
}

/** Whether the browser can offer a passkey inside the e-mail field. */
export async function conditionalMediationAvailable(): Promise<boolean> {
  if (!passkeysSupported()) return false;
  const api = window.PublicKeyCredential as unknown as {
    isConditionalMediationAvailable?: () => Promise<boolean>;
  };
  if (typeof api.isConditionalMediationAvailable !== 'function') return false;
  try {
    return await api.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}

/**
 * What a rejected ceremony means, when it is the BROWSER's outcome rather than
 * ours. `navigator.credentials` reports through DOMException names:
 *
 *   * `NotAllowedError` — the person dismissed the prompt, it timed out, or no
 *     passkey matched; `AbortError` — we called it off ourselves. Neither is a
 *     failure of anything, and reporting it would put an error on screen for
 *     something the person did (or we did) on purpose.
 *   * `InvalidStateError` on registration — the authenticator already holds one
 *     of `excludeCredentials`: this device has a passkey for this account.
 *
 * Anything else (a `SecurityError` for an RP ID the page is not under, a
 * `NotSupportedError`) IS a bug or a misconfiguration and is left to propagate.
 */
export type CeremonyOutcome = 'dismissed' | 'already_registered' | null;

export function ceremonyOutcome(error: unknown): CeremonyOutcome {
  const name = typeof error === 'object' && error !== null ? String((error as { name?: unknown }).name || '') : '';
  if (name === 'NotAllowedError' || name === 'AbortError') return 'dismissed';
  if (name === 'InvalidStateError') return 'already_registered';
  return null;
}
